import { Card } from '../../../molecules/Card/Card';
import {
  isEmpty,
  type ChartBaseProps,
  type ChartRow,
} from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { Gauge } from '../../../molecules/Gauge/Gauge';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { LineChart } from '../../../organisms/LineChart/LineChart';
import { cardState, spanClass, ticksFor, type WidgetBlockProps } from '../shared/cardShell';
import type { ChartTooltipComparison } from '../../../molecules/ChartTooltip/ChartTooltip';

/** NPS is published on a −10…+10 scale (the metric's `NPS_BOUND`). */
const NPS_BOUND = 10;

export type NpsView = {
  /** Positive share (with its delta). */
  kpis: KPIMetric[];
  /** The last point of the *smoothed* daily score — the gauge's headline. */
  npsNow: number;
  /** The gauge's reading over the comparison period; absent with comparison off. */
  gaugeCompare?: ChartTooltipComparison;
  /** The **raw** daily score, one row per bucket. */
  rows: ChartRow[];
  xKey: string;
  series: ChartSeries[];
  /** Whether any mention is in scope — an empty scope is empty even if buckets exist. */
  hasRows: boolean;
  formatValue: (value: number) => string;
  /** Comparison-period reading per row and series; nothing when comparison is off. */
  formatCompare?: ChartBaseProps['formatCompare'];
};

export type NpsCardProps = WidgetBlockProps & {
  view?: NpsView;
  /** Opens the conversations behind a point (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Brand Health's first card (849:29599), and Overview's (972:19373).
 *
 * Two readings of the same metric, deliberately different.
 *
 * The trend line draws the **raw** day-by-day score: a smoothed line would hide
 * exactly the volatility a trend is there to show.
 *
 * The gauge headlines the last point of the *smoothed* series — an EMA carrying
 * each day forward. So the big figure and the line's last point are close but
 * need not be equal, and that is the point: the headline is a position, the
 * line is the history it came from.
 *
 * Positive share alone in the KPI row — NPS itself was dropped from this row on
 * 2026-09-16, because the gauge now carries it as its own readout and the card
 * was printing the same figure twice. The row keeps what the chart does not say.
 */
export function NpsCard({ status, view, span, chrome, onRetry, onViewDetails }: NpsCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmpty(view.rows, view.series);
  return (
    <Card
      /* Set only when this card is a widget on a custom report — see `CardChrome`. */
      {...chrome}
      className={spanClass(span)}
      {...cardState(status, hasData, onRetry)}
      title="NPS"
      helpText="Shows the balance between favourable responses (promoters) and unfavourable responses (detractors), and how it changes over time. Scores range from -10 to +10; higher scores indicate a more favourable balance."
      kpi={view && <KPI metrics={view.kpis} />}
      /* Figma `Card` `State=2 charts` (918:35576) — the gauge over its trend. */
      chart={
        view && (
          <Gauge
            value={view.npsNow}
            compare={view.gaugeCompare}
            bound={NPS_BOUND}
            caption="score now"
            formatValue={view.formatValue}
          />
        )
      }
      chart2={
        view && (
          <LineChart
            data={view.rows}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={view.series}
            /* Both plots share the chart area; only the card knows its height. */
            height="fill"
            legend="none"
            /* Zero is the line that matters on a signed score. */
            threshold={0}
            /*
             * The metric's whole range, pinned. Letting Recharts fit the axis to a
             * quiet week would redraw the same shape at a different scale every
             * time the filters move, and the gauge above is drawn against this
             * same bound.
             */
            yDomain={[-NPS_BOUND, NPS_BOUND]}
            /* The bound, the midpoints and zero — the numbers a signed scale is read by. */
            yTicks={[-NPS_BOUND, -NPS_BOUND / 2, 0, NPS_BOUND / 2, NPS_BOUND]}
            /*
             * Fit the gutter to "+10" rather than to Figma's 40px number, so the
             * labels start at the card's edge and the trend lines up with the bar
             * above it. The default leaves 25px of dead space here.
             */
            yAxisWidth="auto"
            /*
             * The label budget, from the column the module put this card in. At
             * `third` (384) ten date labels want more axis than there is and nine
             * of the ten overlap their neighbour. See `ticksFor`.
             */
            xTicksTarget={ticksFor(span)}
            formatValue={view.formatValue}
            formatAxisValue={(v) => `${v > 0 ? '+' : ''}${Math.round(v)}`}
            formatCompare={view.formatCompare}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
